import { useCallback, useState } from 'react';
import apiService from '../services/api';
import {
  isOnlinePaidPriceLockedForMutation,
  PRICE_LOCK_MESSAGE,
} from '../utils/paiementTenderBreakdown';
import { buildModifiedLignesPoidsReel } from '../utils/variableWeightAdjustmentPayload';

/**
 * Saisie poids réel (PATCH lignes-poids) — parité commerçant / Super Admin.
 */
export function useCommandePoidsAjustement({
  selectedCommande,
  setSelectedCommande,
  refreshCommandeDetail,
  onAfterSave,
  formatEuro,
}) {
  const [poidsDrafts, setPoidsDrafts] = useState({});
  const [poidsComplementUrl, setPoidsComplementUrl] = useState('');
  const [poidsError, setPoidsError] = useState('');
  const [poidsMessage, setPoidsMessage] = useState('');
  const [savingPoids, setSavingPoids] = useState(false);

  const handleSavePoidsAjustement = useCallback(async () => {
    if (!selectedCommande?.id) return;
    if (
      isOnlinePaidPriceLockedForMutation(selectedCommande, {
        mutation: 'variable_weight_adjust',
      })
    ) {
      setPoidsError(PRICE_LOCK_MESSAGE);
      return;
    }
    const lignes = buildModifiedLignesPoidsReel(
      poidsDrafts,
      selectedCommande?.produits,
    );
    if (lignes.length === 0) {
      setPoidsError(
        'Aucune modification de poids réel par rapport aux montants enregistrés',
      );
      return;
    }
    try {
      setSavingPoids(true);
      setPoidsError('');
      setPoidsMessage('');
      const result = await apiService.ajusterPoidsLignesCommande(
        selectedCommande.id,
        lignes,
      );
      if (result?.commande) {
        setSelectedCommande(result.commande);
      } else if (refreshCommandeDetail) {
        await refreshCommandeDetail(selectedCommande.id);
      }
      if (onAfterSave) {
        await onAfterSave();
      }
      const delta = Number(result?.delta) || 0;
      const paiement = result?.paiement || {};
      let msg = `Total commande : ${formatEuro(Number(result?.nouveauTotal || 0))}`;
      if (paiement.netCollected != null) {
        msg += ` · Net encaissé : ${formatEuro(Number(paiement.netCollected))}`;
      }
      if (paiement.action === 'refund' && paiement.refund?.amount) {
        msg += ` · Remboursement Stripe ${formatEuro(Number(paiement.refund.amount))}`;
        setPoidsComplementUrl('');
      } else if (paiement.action === 'complement' && paiement.complement?.amount) {
        msg += ` · Reste à encaisser : ${formatEuro(Number(paiement.complement.amount))}`;
        if (paiement.complement?.checkoutUrl) {
          setPoidsComplementUrl(paiement.complement.checkoutUrl);
        }
      } else if (paiement.action === 'complement_failed') {
        msg += ` · Complément ${formatEuro(Number(paiement.complement?.amount || delta))} à encaisser (Stripe KO)`;
      } else if (delta !== 0) {
        msg += ` · Écart ${delta > 0 ? '+' : ''}${formatEuro(delta)}`;
      } else {
        setPoidsComplementUrl('');
      }
      setPoidsMessage(msg);
    } catch (err) {
      const apiErr = err?.data?.error;
      setPoidsError(
        err?.data?.message ||
          (typeof apiErr === 'object' && apiErr?.message) ||
          (typeof apiErr === 'string' ? apiErr : null) ||
          err?.message ||
          'Erreur lors de l’ajustement poids',
      );
    } finally {
      setSavingPoids(false);
    }
  }, [
    selectedCommande,
    poidsDrafts,
    setSelectedCommande,
    refreshCommandeDetail,
    onAfterSave,
    formatEuro,
  ]);

  return {
    poidsDrafts,
    setPoidsDrafts,
    poidsComplementUrl,
    poidsError,
    poidsMessage,
    savingPoids,
    handleSavePoidsAjustement,
  };
}
